import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { emptyArray } from 'ui-components/default-props/empty';

import Global from '../../store/global-selectors';
import { twelveHourUserSetting } from '../../constants/timezone-format';
import style from './style.module.less';
import messages from './messages';
import { FormattedMessage } from 'react-intl';
import { DisplayActivityEntry } from './display-activity-entry-component';
import { getFulfillmentPlanActivity } from './fulfillment-plan-activity.api';
import classNames from 'classnames';

const calculateUserDateTimeFormat = (dateFormat, timeFormat) => {
  const userTimeFormat = timeFormat === twelveHourUserSetting ? 'h:mm a' : 'H:mm';
  return `${dateFormat} ${userTimeFormat}`;
};

const sortActivityEntriesByCreateDateTimeAscending = (entries) => {
  if (entries.length < 2) {
    return entries;
  }
  return entries.sort((a, b) => {
    if (a.createDateTime < b.createDateTime) return -1;
    if (a.createDateTime > b.createDateTime) return 1;
    return 0;
  });
};

const FulfillmentPlanActivityComponent = ({ fpid, className = '' }) => {
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const userTimeFormat = useSelector(Global.selectUserUserTimeFormat);
  const userDateFormat = useSelector(Global.selectUserUserDateFormat);
  const userDateTimeFormat = calculateUserDateTimeFormat(userDateFormat, userTimeFormat);
  const [activityData, setActivityData] = useAsyncSafeState(emptyArray);
  const sortedActivityEntries = useMemo(() => sortActivityEntriesByCreateDateTimeAscending(activityData), [activityData]);
  const [loadIndex, setLoadIndex] = useAsyncSafeState(0);
  const loadedEntries = (sortedActivityEntries || []).slice(0, loadIndex + 5);

  useEffect(() => {
    getFulfillmentPlanActivity(fpid).then((data) => {
      setActivityData(data?.fulfillmentPlanActivityLogData || emptyArray);
    });
  }, [fpid, setActivityData]);

  const loadMore = useCallback(() => {
    setLoadIndex(loadIndex + 5);
  }, [loadIndex, setLoadIndex]);

  return (
    <div className={classNames(style['fulfillment-plan-activity-list'], className)}>
      {loadedEntries.length
        ? loadedEntries?.map((activityEntry, index) => (
            <DisplayActivityEntry
              key={index}
              activityEntry={activityEntry}
              sellerTimeZone={sellerTimeZone}
              userDateTimeFormat={userDateTimeFormat}
            />
          ))
        : null}

      {sortedActivityEntries?.length > loadedEntries.length && (
        <Button className={style['button-load-all']} onClick={() => loadMore()} theme='link'>
          <FormattedMessage {...messages.loadMore} />
        </Button>
      )}
    </div>
  );
};

FulfillmentPlanActivityComponent.propTypes = {
  fpid: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default FulfillmentPlanActivityComponent;
